import { makeStyles } from '@mui/styles'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import theme from '@/theme'
import lang from '@/lang'
import TenantInitialsBubble from '@/modules/my-account/tenant-information/tenant-initials-bubble'
import ThemeInterface from '@/theme-interface'
import IconCheck from '@assets/images/icons/check.svg'
import IconCancel from '@assets/images/icons/close.svg'
import IconPencil from '@assets/images/icons/pencil.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { TextInput } from '@/modules/base/components/inputs'
import { ButtonIconSquare } from '@/modules/base/components/buttons'
import { Form, Formik } from 'formik'
import { closeTenantSelection } from '@/redux/ducks/tenant/selection'
import { setTenantName } from '@/redux/ducks/user/info'
import { useNavigate } from 'react-router'
import { useAppDispatch } from '@/redux/store'

const iconHeight = 25
const iconWidth = 25

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'grid',
    gridTemplateAreas: '"initials details action"',
    gridTemplateColumns: 'auto 1fr auto',
  },
  initials: {
    gridArea: 'initials',
    paddingRight: 16,
    display: 'flex',
    alignItems: 'center',
  },
  initialsBubble: {
    width: 24,
    height: 24,
    border: `1px solid ${theme.iconColor}`,
  },
  details: {
    gridArea: 'details',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  tenantId: {
    color: theme.labelLight,
    fontSize: theme.fontSizes.smaller,
  },
  action: {
    gridArea: 'action',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  actionIcon: {
    '& > *': {
      fill: theme.mode.color.default,
    },
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  inputForm: {
    display: 'flex',
    height: 25,
    marginTop: 0,
    marginBottom: 0,
  },
  inputField: {
    '& > div > input': {
      height: '25px !important',
    },
  },
  iconCheck: {
    width: iconWidth,
    marginLeft: 10,
    '& > div': {
      width: iconWidth,
      minWidth: iconWidth,
      height: iconHeight,
    },
  },
  iconCancel: {
    marginLeft: 5,
    width: iconWidth,
    '& > div': {
      background: `${theme.redStateColor} !important`,
      width: iconWidth,
      minWidth: iconWidth,
      height: iconHeight,
      '&:hover': {
        background: theme.superlightGrey,
      },
    },
  },
}), { name: 'TenantSelectionItemActive' })

const TenantSelectionItemActive = () => {
  const navigate = useNavigate()
  const classes = useStyles()
  const dispatch = useAppDispatch()
  const [editModeEnabled, setEditModeEnabled] = useState<boolean>(false)
  const {
    tenantId,
    tenantName,
    companyName,
    color,
    backgroundColor,
  } = useSelector(state => state.user.info.activeTenant)
  const iconColors = useMemo(() => ({
    color: color ?? theme.white,
    backgroundColor: backgroundColor ?? theme.primary,
  }), [color, backgroundColor])
  const tenantInitialsStyles = useMemo(() => {
    return { root: classes.initialsBubble }
  }, [classes])

  const navigateToSettings = (): void => {
    dispatch(closeTenantSelection())
    navigate('/company')
  }
  const onEdit = (): void => {
    setEditModeEnabled(true)
  }
  const onSubmit = ({ tenantName }): void => {
    dispatch(setTenantName(tenantName))
    setEditModeEnabled(false)
  }
  const onCancel = (): void => {
    setEditModeEnabled(false)
  }

  const nameComponent = editModeEnabled
    ? (
      <div className={classes.inputContainer} >
        <Formik
          initialValues={{ tenantName: tenantName || companyName }}
          onSubmit={onSubmit}
          enableReinitialize={false} >
          <Form className={classes.inputForm}>
            <TextInput
              name='tenantName'
              className={classes.inputField}
              autoFocus={true}
              useFormik
            />
            <ButtonIconSquare
              icon={IconCheck}
              type='submit'
              className={classes.iconCheck}
              iconFill={theme.white}
            />
            <ButtonIconSquare
              icon={IconCancel}
              className={classes.iconCancel}
              onClick={onCancel}
              iconFill={theme.white}
            />
          </Form>
        </Formik>
      </div>
    )
    : <span>{tenantName || companyName || lang.tenant.newTenant}</span>

  return (
    <div className={classes.root}>
      <div className={classes.initials}>
        <TenantInitialsBubble
          styles={tenantInitialsStyles}
          iconColors={iconColors}
          tenantName={tenantName}
          companyName={companyName}
          onClick={navigateToSettings} />
      </div>
      <div className={classes.details}>
        {nameComponent}
        <span className={classes.tenantId}>{lang.detailLabels.number} {tenantId}</span>
      </div>
      <div className={classes.action}>
        {editModeEnabled
          ? null
          : (
            <div className={classes.action} onClick={onEdit}>
              <SVGIcon className={classes.actionIcon} svg={IconPencil} height="14px" width="14px" />
            </div>
          )
        }
      </div>
    </div>
  )
}

export default TenantSelectionItemActive
